"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import Script from "next/script";

import {
  consumeGaLoginCookie,
  consumeGaSignupCookie,
  gaCampaignParams,
  gaPageLocation,
  installGaApiTracking,
  isGaInternal,
  isInternalGaUser,
  markInternalTraffic,
  takeLoginSuppression,
  trackGaEvent,
} from "@/lib/ga";

const SKIP_PREFIXES = ["/admin", "/api"];

function isValidGaId(id: string | undefined): id is string {
  return typeof id === "string" && /^G-[A-Z0-9]+$/.test(id);
}

function sendPageView(pathname: string) {
  if (isGaInternal() || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: pathname,
    page_location: gaPageLocation(),
    page_title: document.title,
    ...gaCampaignParams(),
  });
}

/**
 * Loads GA4 (gtag.js) and records a page view on each App Router navigation.
 * Query strings are omitted so reset-password / invite tokens never go to Google.
 * Skips admin and API routes, and browsers marked as internal team traffic.
 * Renders nothing if NEXT_PUBLIC_GA_MEASUREMENT_ID is unset.
 *
 * sign_up is the registration event. Mark it as a key event in GA4
 * (Admin → Data display → Events) once it shows up in the property.
 */
export function GoogleAnalytics() {
  const gaId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  const pathname = usePathname() || "/";
  const skipFirst = useRef(true);
  const { data: session, status } = useSession();
  const prevStatus = useRef(status);
  const sessionUser = session?.user as
    | { role?: string | null; roles?: string[] | null; isSeedAdmin?: boolean | null }
    | undefined;
  const sessionRole = sessionUser?.role ?? "";
  const sessionRoles = (sessionUser?.roles || []).join(",");
  const sessionSeedAdmin = sessionUser?.isSeedAdmin === true;

  useEffect(() => {
    if (!isValidGaId(gaId)) return;
    return installGaApiTracking();
  }, [gaId]);

  useEffect(() => {
    if (!isValidGaId(gaId) || status === "loading") return;
    const internal = isInternalGaUser(sessionUser);
    if (internal) markInternalTraffic();
    if (internal || isGaInternal()) {
      consumeGaSignupCookie();
      consumeGaLoginCookie();
      takeLoginSuppression();
      prevStatus.current = status;
      return;
    }
    const signupMethod = consumeGaSignupCookie();
    if (signupMethod) {
      trackGaEvent("sign_up", { method: signupMethod });
      consumeGaLoginCookie();
      prevStatus.current = status;
      return;
    }
    const loginMethod = consumeGaLoginCookie();
    const justSignedIn = prevStatus.current === "unauthenticated" && status === "authenticated";
    prevStatus.current = status;
    if ((loginMethod || justSignedIn) && !takeLoginSuppression()) {
      trackGaEvent("login", { method: loginMethod || "credentials" });
    }
  }, [gaId, status, sessionUser, sessionRole, sessionRoles, sessionSeedAdmin]);

  useEffect(() => {
    if (!isValidGaId(gaId)) return;
    // The inline gtag config already sends the first hit once the script loads.
    if (skipFirst.current) {
      skipFirst.current = false;
      return;
    }
    if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return;
    sendPageView(pathname);
  }, [gaId, pathname]);

  if (!isValidGaId(gaId)) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          function clGaLocation() {
            var keep = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','utm_id','gclid','gbraid','wbraid'];
            var params = new URLSearchParams(location.search);
            var out = new URLSearchParams();
            keep.forEach(function(k) {
              var v = params.get(k);
              if (v) out.set(k, v);
            });
            var q = out.toString();
            return location.origin + location.pathname + (q ? '?' + q : '');
          }
          (function persistAcq() {
            var keep = ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','gclid','gbraid','wbraid'];
            var params = new URLSearchParams(location.search);
            var out = new URLSearchParams();
            keep.forEach(function(k) {
              var v = params.get(k);
              if (v) out.set(k, v);
            });
            if (!out.toString() || document.cookie.split('; ').some(function(c) { return c.indexOf('cl_acq=') === 0; })) return;
            document.cookie = 'cl_acq=' + encodeURIComponent(out.toString()) + '; Max-Age=' + (90*24*60*60) + '; Path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
          })();
          if (!document.cookie.split('; ').some(function(c) { return c.indexOf('cl_ga_internal=1') === 0; })) {
          gtag('js', new Date());
          gtag('config', '${gaId}', {
            send_page_view: false,
            page_path: location.pathname,
            page_location: clGaLocation()
          });
          gtag('config', 'AW-18426426561');
          if (!location.pathname.startsWith('/admin') && !location.pathname.startsWith('/api')) {
            var campaign = {};
            var sp = new URLSearchParams(location.search);
            if (sp.get('utm_source')) campaign.campaign_source = sp.get('utm_source');
            if (sp.get('utm_medium')) campaign.campaign_medium = sp.get('utm_medium');
            if (sp.get('utm_campaign')) campaign.campaign_name = sp.get('utm_campaign');
            if (sp.get('utm_term')) campaign.campaign_term = sp.get('utm_term');
            if (sp.get('utm_content')) campaign.campaign_content = sp.get('utm_content');
            gtag('event', 'page_view', Object.assign({
              page_path: location.pathname,
              page_location: clGaLocation(),
              page_title: document.title
            }, campaign));
          }
          }
        `}
      </Script>
    </>
  );
}
